import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import fetchIframeForVideo from '@aurora/shared-client/components/occasions/FetchIframeForVideo.query.graphql';
import useCrossOriginAttribute from '@aurora/shared-client/components/useCrossOriginAttribute';
import useIntersectionFix from '@aurora/shared-client/components/useIntersectionFix';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { getExternalVideoConsentCookie } from '@aurora/shared-client/helpers/cookie/CookieHelper';
import { getVideoThumbnailUrl } from '@aurora/shared-client/helpers/video/VideoPreviewHelper';
import type { OccasionTopicMessage } from '@aurora/shared-generated/types/graphql-schema-types';
import React, { useContext, useRef, useState } from 'react';
import { Image, useClassNameMapper } from 'react-bootstrap';
import type {
  FetchIframeForVideoQuery,
  FetchIframeForVideoQueryVariables
} from '../../../types/graphql-types';
import VideoConsentBanner from '../../common/VideoConsentBanner/VideoConsentBanner';
import useAutoRefresh from '../../useAutoRefresh';
import localStyles from './OccasionVideoPreview.module.pcss';

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * The live stream url when creating an occasion
   */
  createOccasionLiveStreamUrl?: string;
  /**
   * Whether this is rendered from create/edit view
   */
  isCreateOrEditView?: boolean;
}

/**
 * Renders a video preview component for an occasion.
 *
 * @author Matt Vrydaghs, Akash Goenka
 */
const OccasionVideoPreview: React.FC<Props> = ({
  className,
  createOccasionLiveStreamUrl = null,
  isCreateOrEditView = false
}) => {
  const cx = useClassNameMapper(localStyles);
  const { contextMessage } = useContext(AppContext);
  const tenant = useContext(TenantContext);
  const { liveVideoIframe, startTime, endTime, liveStreamUrl } =
    (contextMessage as OccasionTopicMessage)?.occasionData ?? {};
  const isOccasionInProgress = useAutoRefresh(startTime, endTime, liveVideoIframe, 3600000);
  const liveVideoRef = useRef<HTMLDivElement>(null);
  const spyRef = useRef<HTMLDivElement>(null);
  const externalVideoConsentProvided = getExternalVideoConsentCookie();
  const [showBanner, setShowBanner] = useState<boolean>(!externalVideoConsentProvided);
  const crossOrigin = useCrossOriginAttribute();
  const occasionLiveStreamUrl = createOccasionLiveStreamUrl ?? liveStreamUrl;

  const queryResult = useQueryWithTracing<
    FetchIframeForVideoQuery,
    FetchIframeForVideoQueryVariables
  >(module, fetchIframeForVideo, {
    variables: {
      url: occasionLiveStreamUrl,
      autoPlay: isOccasionInProgress
    },
    skip: !occasionLiveStreamUrl
  });

  const intersection = useIntersectionFix(spyRef, {
    root: null,
    threshold: [0.1, 0.4, 0.6, 1]
  });

  const fixedPosition: boolean =
    intersection &&
    intersection.intersectionRatio < 0.5 &&
    isOccasionInProgress &&
    !isCreateOrEditView;

  if (queryResult?.loading || !queryResult?.data?.fetchIframeForVideo?.result) {
    return null;
  }

  const { result: iframeSource } = queryResult?.data?.fetchIframeForVideo;
  const thumbnailUrl = getVideoThumbnailUrl(occasionLiveStreamUrl, iframeSource);

  return (
    <>
      <div className={cx('lia-reference')} ref={spyRef} aria-hidden="true" />
      {tenant.publicConfig.showExternalVideoCookieBanner && !externalVideoConsentProvided ? (
        <div className={cx('lia-container', className)} data-testid="OccasionVideoPreviewThumbnail">
          {thumbnailUrl ? (
            <Image
              className={cx('lia-video-wrap')}
              src={thumbnailUrl}
              alt={liveStreamUrl}
              crossOrigin={crossOrigin(thumbnailUrl)}
            />
          ) : (
            <div className={cx('lia-video-wrap lia-placeholder')} />
          )}
          {showBanner && (
            <VideoConsentBanner
              onHide={() => setShowBanner(false)}
              occasionLiveStreamUrl={occasionLiveStreamUrl}
            />
          )}
        </div>
      ) : (
        <div
          ref={liveVideoRef}
          data-testid="OccasionVideoPreview"
          dangerouslySetInnerHTML={{ __html: iframeSource }}
          className={cx('lia-video-wrap', className, { 'lia-video-is-fixed': fixedPosition })}
        />
      )}
    </>
  );
};

export default OccasionVideoPreview;
